import { JSX, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { faCheck, faXmark } from "@fortawesome/free-solid-svg-icons";
import { useFetchStar } from "../../../hooks/useFetchStar";
import Translator from "../../components/translator";

export const FuneralRSVPPage = () => {
  const { starUuid } = useParams();

  const { fetchStar, starLoading, star, funeral } = useFetchStar(starUuid as string);

  useEffect(() => {
    fetchStar();
  }, [fetchStar]);

  if (starLoading || !star) {
    return <FuneralRSVPLoadingState />;
  }

  const hasBackground = !!star.background;

  return (
    <div className="min-h-screen bg-gray-100 flex justify-center">
      <div className="w-full max-w-lg min-h-screen bg-white relative overflow-hidden shadow-2xl">

        <StarsDetailSubheader star={star} />

        <div
          className={
            `relative bg-white rounded-t-[32px] px-6 pb-8 pt-8 text-center ${hasBackground ? "-mt-8" : "mt-6"}`
          }
        >
          <p className="text-sm font-bold text-[#868686]">
            <Translator path="star.inLovingMemoryOf" />
          </p>

          <h1 className="mt-2 text-[28px] leading-[34px] font-medium text-black">
            {star.firstName} {star.lastName}
          </h1>

          <div className="mt-6 flex flex-wrap justify-center gap-4 text-sm text-[#5E5E5E]">
            <div className="flex items-center gap-2">
              <img
                src="/imgs/sparkle.svg"
                alt=""
                className="h-5 w-5"
              />

              <span>{formatLongDate(star.dateOfBirth)}</span>
            </div>

            <div className="flex items-center gap-2">
              <img
                src="/imgs/cross.svg"
                alt=""
                className="h-5 w-5"
              />
              <span>{formatLongDate(star.dateOfDeath)}</span>
            </div>
          </div>

          <p className="mt-8 text-base leading-[21px] text-[#5E5E5E]">
            <Translator path="star.weWillBeGivingOurFuneralOn" values={{ date: formatLongDate(funeral?.date), time: formatLongTime(funeral?.time) }} />
          </p>

          {funeral?.document && (
            <button
              onClick={() => window.open(funeral.document, "_blank")}
              className="mt-6 inline-flex items-center gap-2 rounded-full border border-[#8C7956] bg-white px-5 py-3 font-semibold text-[#8C7956] transition hover:bg-[#EAE6DD]"
            >
              <img
                src="/imgs/cardHeart.svg"
                alt=""
                className="h-5 w-5"
              />
              <Translator path="star.viewFuneralCard" />
            </button>
          )}

          <RSVPSection star={star} funeral={funeral} />
        </div>
      </div>
    </div>
  );
};

const StarsDetailSubheader = ({ star }: { star: any }) => {
  const hasBackground = !!star.background;

  return (
    <div className="w-full flex flex-col items-center">
      {hasBackground ? (
        <div className="relative h-[400px] w-full flex items-center justify-center">
          <img
            src={star.background}
            alt=""
            className="absolute inset-0 h-full w-full object-cover"
          />

          <StarAvatar star={star} />
        </div>
      ) : (
        <div className="pt-24">
          <StarAvatar star={star} />
        </div>
      )}
    </div>
  );
};

const StarAvatar = ({ star }: { star: any }) => {
  const imageUrl = star.photo

  return (
    <div className="relative z-10 h-36 w-36 rounded-full bg-gray-100 border-4 border-white shadow overflow-hidden">
      {imageUrl ? (
        <img
          src={imageUrl}
          alt={`${star.firstName} ${star.lastName}`}
          className="h-full w-full object-cover"
        />
      ) : (
        <div className="h-full w-full flex items-center justify-center text-4xl font-semibold text-gray-400">
          {star.firstName?.[0]}
        </div>
      )}
    </div>
  );
};

const RSVPSection = ({ star, funeral }: { star: any; funeral: any }) => {
  const navigate = useNavigate();

  return (
    <div className="mt-6">
      <>
        <p className="text-xl leading-[30px] font-semibold text-[#5E5E5E]">
          <Translator path="star.ourFamilyWouldLikeToKnowIfYouWillBeAttendingTheFuneral" />
        </p>

        <div className="mt-4 flex justify-center gap-12">
          <AcceptanceOption
            label={<Translator path="attendees.present" />}
            icon={faCheck}
            className="text-green-600 border-green-600 hover:bg-green-100"
            onClick={() => {

              navigate(
                `/${funeral?.uuid}/attendance`,
                {
                  state: {
                    starName: `${star.firstName} ${star.lastName}`
                  }
                }
              );
            }}
          />

          <AcceptanceOption
            label={<Translator path="attendees.absent" />}
            icon={faXmark}
            className="text-error border-error hover:bg-error/20"
            onClick={() => {

              navigate(
                `/invite/decline`,
                {
                  state: {
                    starName: `${star.firstName} ${star.lastName}`
                  }
                }
              );
            }}
          />
        </div>
      </>
    </div>
  );
};

const AcceptanceOption = ({
  label,
  icon,
  className,
  onClick,
}: {
  label: string | JSX.Element;
  icon: any;
  className: string;
  onClick: () => void;
}) => {
  return (
    <button onClick={onClick} className="flex flex-col items-center">
      <div
        className={`h-20 w-20 rounded-full border flex items-center justify-center ${className}`}
      >
        <FontAwesomeIcon icon={icon} className="text-3xl" />
      </div>

      <span className="mt-2 text-base font-bold text-[#5E5E5E]">{label}</span>
    </button>
  );
};

export const FuneralRSVPLoadingState = () => {
  return (
    <div className="fixed inset-0 flex items-center justify-center bg-white">
      <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-primary border-opacity-75" />
    </div>
  );
};

const formatLongDate = (value?: Date) => {
  if (!value) return "";

  return new Intl.DateTimeFormat("en", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  }).format(new Date(value));
};

const formatLongTime = (value?: string) => {
  if (!value) return "";

  return value;
};
